<template>
  <div class="credibility-section section">
    <h2 class="section-title">
      <span class="icon"><i class="mdi mdi-shield-check"></i></span>
      Teamwrkr Score
    </h2>

    <div class="trust-score-container">
      <template v-if="hasActivityScore">
        <h3 class="sub-heading centered">
          <span class="icon"><i class="mdi mdi-star-circle"></i></span>
          Community
        </h3>
        <div class="trust-meter-wrapper" @mouseenter="showTooltip = true" @mouseleave="showTooltip = false">
          <div class="trust-meter">
            <div class="meter-track"></div>
            <div
              class="meter-fill"
              :style="{
                width: `${calculateScorePercentage(activityScore.activity_score)}%`,
                background: getMeterColor(activityScore.activity_score)
              }"
            ></div>
            <div class="score-value">
              {{ parseFloat(activityScore.activity_score).toFixed(2) }}
              <span class="score-max">/10</span>
            </div>
          </div>
          <div class="trust-score-tooltip" v-if="showTooltip">
            <h4 class="tooltip-title">Community Score Breakdown</h4>
            <div class="score-breakdown">
              <div class="breakdown-item">
                <span class="label">Presence:</span>
                <span class="value">{{ parseFloat(activityScore.presence).toFixed(2) }}</span>
              </div>
              <div class="breakdown-item">
                <span class="label">Participation:</span>
                <span class="value">{{ parseFloat(activityScore.participation).toFixed(2) }}</span>
              </div>
              <div class="breakdown-item">
                <span class="label">Contribution:</span>
                <span class="value">{{ parseFloat(activityScore.contribution).toFixed(2) }}</span>
              </div>
              <div class="breakdown-item">
                <span class="label">Connection:</span>
                <span class="value">{{ parseFloat(activityScore.connection).toFixed(2) }}</span>
              </div>
            </div>
            <div class="score-breakdown-note">
              <span>
                <strong>Community Score = Average of Presence, Participation, Contribution, Connection</strong>
              </span>
            </div>
          </div>
        </div>
        <div class="trust-score-description">
          <p>
            Your community score is the average of your Presence, Participation, Contribution, and Connection scores.
          </p>
        </div>
      </template>
      <template v-else>
        <div class="trust-score-empty">
          <i class="mdi mdi-shield-off-outline empty-icon"></i>
          <p>Teamwrkr Score data is not yet available because this person is new to the platform.</p>
        </div>
      </template>

      <!-- Badges Section -->
      <h3 class="sub-heading centered">
        <span class="icon"><i class="mdi mdi-medal"></i></span>
        Badges
      </h3>
      <div v-if="hasPeerBadge" class="badges-section">
        <div class="verification-badges centered">
          <span
            class="badge peer-badge"
            @mouseenter="showPeerBadgeTooltip = true"
            @mouseleave="showPeerBadgeTooltip = false"
          >
            <i class="mdi mdi-account-group"></i>
            Peer Verified
            <span
              v-if="showPeerBadgeTooltip"
              class="badge-tooltip"
            >
              This Teamwrkr has received 3 Peer verifications of their Profile.
            </span>
          </span>
        </div>
      </div>
      <div v-else class="badges-section">
        <!-- <div class="verification-badges centered"> -->
          <p>No badges earned yet. Get verified to earn badges!</p>
          <b-button
            v-if="!isPublicProfile"
            class="review-launch-btn"
            @click="showVerifiedModal = true"
          >
            <i class="mdi mdi-star"></i>
            Get Verified
          </b-button>
        <!-- </div> -->
      </div>
    </div>
  </div>
  <!-- Modals and Router -->
  <get-verified-modal
    v-if="user && user._id"
    :user="user"
    v-model:showModal="showVerifiedModal"
  />
</template>

<script>
import GetVerifiedModal from './GetVerifiedModal.vue';
import { useUserStore } from '@/stores/userStore';
import { mapState } from 'pinia'

export default {
  name: 'TrustScoreMeter',
  components: {
    GetVerifiedModal,
  },
  props: {
    activityScore: {
      type: Object,
      required: true
    },
    reviews: {
      type: Array,
      required: false,
      default: () => []
    },
    isVerified: {
      type: Boolean,
      default: false
    },
    isPublicProfile: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showTooltip: false,
      showPeerBadgeTooltip: false,
      showVerifiedModal: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['user']),
    hasActivityScore() {
      return (
        this.activityScore &&
        Object.keys(this.activityScore).length > 0 &&
        typeof this.activityScore.activity_score !== 'undefined'
      );
    },
    accuratePeerReviews() {
      return this.reviews.filter(
        r =>
          r.type === 'peer' &&
          r.status === 'completed' &&
          r.isAccurate === true
      );
    },
    hasPeerBadge() {
      return this.accuratePeerReviews.length >= 3;
    }
  },
  methods: {
    calculateScorePercentage(score) {
      const percentage = (parseFloat(score) / 10) * 100;
      return Math.min(Math.max(percentage, 0), 100);
    },
    getMeterColor(score) {
      const val = parseFloat(score);
      if (val < 2) return '#e5462d'; // red
      if (val < 4) return '#ff9800'; // orange
      if (val < 6) return '#efbf41'; // yellow
      if (val < 8) return '#1b9aaa'; // blue
      return '#aa76d6'; // purple
    }
  }
};
</script>

<style lang="scss" scoped>
.credibility-section.section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: $color-text;
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $color-border-muted;

  .icon {
    margin-right: 12px;
    color: $color-primary;
  }
}

.sub-heading {
  display: flex;
  align-items: center;
  justify-content: center;
  color: $color-text;
  font-size: 1.32rem;
  font-weight: 700;
  margin-bottom: 1rem;
  margin-top: 1.2rem;
  .icon {
    margin-right: 8px;
    color: $color-primary;
    font-size: 1.3rem;
  }
}
.centered {
  justify-content: center;
  text-align: center;
}

.trust-score-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.trust-meter-wrapper {
  position: relative;
  width: 100%;
  margin-bottom: 1.5rem;
}

.trust-meter {
  height: 40px;
  width: 100%;
  background-color: $color-surface-soft;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.1);
}

.meter-track {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: repeating-linear-gradient(
    to right,
    transparent,
    transparent 9%,
    rgba(0, 0, 0, 0.05) 10%
  );
}

.meter-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  transition: width 1s ease-out, background 0.4s;
  border-radius: 20px;
}

.score-value {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: $color-text;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);

  .score-max {
    font-size: 1rem;
    opacity: 0.7;
    margin-left: 2px;
  }
}

.trust-score-tooltip {
  position: absolute;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  width: 320px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  z-index: 10;
  opacity: 1;
  visibility: visible;
  transition: all 0.3s ease;

  .tooltip-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.8rem;
    text-align: center;
  }

  .score-breakdown {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .breakdown-item {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    margin-bottom: 0.2rem;

    .label {
      width: 120px;
      text-align: left;
      font-weight: 500;
    }
    .value {
      width: 60px;
      text-align: right;
      font-weight: 600;
      margin-left: 0.5rem;
    }
  }

  .score-breakdown-note {
    margin-top: 1rem;
    font-size: 0.95rem;
    color: $color-text-muted;
    text-align: left;
    line-height: 1.5;
  }
}

.trust-score-description {
  margin-top: 1rem;
  p {
    color: $color-text-subtle;
    font-size: 0.95rem;
  }
}

.trust-score-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: $color-text-subtle;
  font-size: 1.1rem;
  padding: 2rem 0;

  .empty-icon {
    font-size: 2.5rem;
    color: $color-text-subtle;
    margin-bottom: 1rem;
  }
}

.badges-section {
  width: 100%;
  margin-top: 1.5rem;
}

.verification-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 0.7rem;
  gap: 1rem;
  .badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    background: $color-surface-alt;
    border-radius: 1.8rem;
    font-size: 1.15rem;
    font-weight: 700;
    padding: 0.5rem 1.5rem 0.5rem 1rem;
    margin-left: 0.5rem;
    box-shadow: 0 1px 4px rgba(170, 118, 214, 0.06);
    cursor: pointer;
    i {
      margin-right: 0.7rem;
      color: $color-primary;
      font-size: 1.5rem;
    }
    .badge-tooltip {
      position: absolute;
      left: 50%;
      top: 110%;
      transform: translateX(-50%);
      background: $color-surface;
      color: $color-text;
      font-size: 1rem;
      font-weight: 400;
      padding: 0.9rem 1.2rem;
      border-radius: 0.9rem;
      box-shadow: 0 2px 12px rgba(170, 118, 214, 0.12);
      white-space: pre-line;
      z-index: 20;
      min-width: 220px;
      text-align: center;
      pointer-events: none;
    }
  }
}

.review-launch-btn {
  margin-top : 0.5rem;
  background-color: $color-primary;
  border-color: transparent;
  color: $color-surface;
}


@media screen and (max-width: 768px) {
  .verification-badges .badge {
    font-size: 0.95rem;
    padding: 0.4rem 1rem 0.4rem 0.7rem;
    border-radius: 1.2rem;
    i {
      font-size: 1.1rem;
      margin-right: 0.5rem;
    }
    .badge-tooltip {
      font-size: 0.92rem;
      padding: 0.6rem 0.8rem;
      border-radius: 0.7rem;
      min-width: 140px;
    }
  }
}
</style>
